import * as THREE from 'three'

import { WORLD_Z_PER_Y } from '../shared/shared'

export class TileLayer {
  constructor(scene, atlas, lights, { z, ySort = false, max = 40000 }) {
    this.scene = scene
    this.atlas = atlas
    this.lights = lights
    this.z = z
    this.ySort = ySort
    this.max = max
    this.tiles = new Map()
    this.pendingTiles = new Map()
    this.dirty = false

    const geo = new THREE.PlaneGeometry(1, 1)
    geo.translate(0, 0.5, 0)

    const uvArr = new Float32Array(max * 4)
    const animArr = new Float32Array(max * 3)
    const sortOffsetArr = new Float32Array(max)
    geo.setAttribute('uvRect', new THREE.InstancedBufferAttribute(uvArr, 4))
    geo.setAttribute('animData', new THREE.InstancedBufferAttribute(animArr, 3))
    geo.setAttribute('aSortOffset', new THREE.InstancedBufferAttribute(sortOffsetArr, 1))

    const mat = this._createMaterial()

    this.mesh = new THREE.InstancedMesh(geo, mat, max)
    this.mesh.frustumCulled = false
    this.mesh.count = 0
    this.scene.add(this.mesh)

    this.uvArr = uvArr
    this.animArr = animArr
    this.sortOffsetArr = sortOffsetArr
    this.dummy = new THREE.Object3D()

    this.atlas.onReady(url => {
      this.mesh.material.uniforms.uAtlas.value = this.atlas.texture
      let didApplyPending = false
      for (const [key, pending] of this.pendingTiles) {
        if (pending.sprite !== url) continue
        this.tiles.set(key, pending)
        this.pendingTiles.delete(key)
        didApplyPending = true
      }
      if (didApplyPending) {
        this.dirty = true
      }
      for (const t of this.tiles.values()) {
        if (t.sprite === url) {
          this.dirty = true
          return
        }
      }
    })
  }

  _createMaterial() {
    return new THREE.ShaderMaterial({
      uniforms: {
        uAtlas: { value: this.atlas.texture },
        uTime: { value: 0 },
        uAlphaTest: { value: 0.5 },
        uCameraY: this.lights.client.uniforms.uCameraY,
        uWorldZPerY: { value: WORLD_Z_PER_Y },
        uLayerZ: { value: this.z },
        uYSort: { value: this.ySort ? 1 : 0 },
        ...this.lights.uniforms,
      },
      vertexShader: /* glsl */ `
        precision highp float;
        attribute vec4 uvRect;
        attribute vec3 animData;
        attribute float aSortOffset;
        uniform float uTime;
        uniform float uCameraY;
        uniform float uWorldZPerY;
        uniform float uLayerZ;
        uniform float uYSort;
        varying vec2 vUv;
        varying vec2 vWorldPos;
        void main() {
          float frameCount = animData.x;
          float fps = animData.y;
          float strideU = animData.z;
          float frame = 0.0;
          if (frameCount > 1.0 && fps > 0.0) {
            frame = floor(mod(uTime * fps, frameCount));
          }
          vec2 mn = vec2(uvRect.x + frame * strideU, uvRect.y);
          vUv = mix(mn, mn + uvRect.zw, uv);
          float sortZ = uLayerZ;
          if (uYSort > 0.5) {
            float pivotY = instanceMatrix[3].y + aSortOffset;
            sortZ += (uCameraY - pivotY) * uWorldZPerY;
          }
          vec3 localPos = vec3(position.xy, position.z + sortZ);
          vec4 worldPos = instanceMatrix * vec4(localPos, 1.0);
          vWorldPos = worldPos.xy;
          gl_Position = projectionMatrix * modelViewMatrix * worldPos;
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        uniform sampler2D uAtlas;
        uniform float uAlphaTest;
        varying vec2 vUv;
        varying vec2 vWorldPos;
        ${this.lights.UNIFORMS_GLSL}
        ${this.lights.FUNCTION_GLSL}
        void main() {
          vec4 c = texture2D(uAtlas, vUv);
          if (c.a < uAlphaTest) discard;
          c.rgb = applyLighting(c.rgb, vWorldPos);
          gl_FragColor = c;
        }
      `,
      transparent: false,
      depthWrite: true,
      depthTest: true,
      side: THREE.DoubleSide,
    })
  }

  set(x, y, sprite, fps, yBase = 0) {
    const key = `${x},${y}`
    const next = { x, y, yBase, sprite, fps: fps || 0 }
    const entry = this.atlas.resolve(sprite, 1, 1)
    if (entry?.ready) {
      this.pendingTiles.delete(key)
      this.tiles.set(key, next)
      this.dirty = true
      return
    }
    this.pendingTiles.set(key, next)
  }

  clear(x, y) {
    const key = `${x},${y}`
    this.pendingTiles.delete(key)
    if (this.tiles.delete(key)) {
      this.dirty = true
    }
  }

  clearAll() {
    this.tiles.clear()
    this.pendingTiles.clear()
    this.dirty = true
  }

  rebuild() {
    this.mesh.material.uniforms.uAtlas.value = this.atlas.texture

    let count = 0
    for (const t of this.tiles.values()) {
      if (count >= this.max) break
      this.atlas.resolve(t.sprite, 1, 1)
      const uv = this.atlas.getUV(t.sprite)
      if (!uv) continue

      this.dummy.position.set(t.x, t.y, 0)
      this.dummy.updateMatrix()
      this.mesh.setMatrixAt(count, this.dummy.matrix)

      const i4 = count * 4
      this.uvArr[i4] = uv.u
      this.uvArr[i4 + 1] = uv.v
      this.uvArr[i4 + 2] = uv.frameW
      this.uvArr[i4 + 3] = uv.frameH

      const fps = t.fps || (uv.frameCount > 1 ? 4 : 0)
      const i3 = count * 3
      this.animArr[i3] = uv.frameCount
      this.animArr[i3 + 1] = fps
      this.animArr[i3 + 2] = uv.strideU
      this.sortOffsetArr[count] = t.yBase || 0

      count++
    }

    this.mesh.count = count
    this.mesh.instanceMatrix.needsUpdate = true
    this.mesh.geometry.attributes.uvRect.needsUpdate = true
    this.mesh.geometry.attributes.animData.needsUpdate = true
    this.mesh.geometry.attributes.aSortOffset.needsUpdate = true
    this.dirty = false
  }

  update(delta, time) {
    if (this.dirty) this.rebuild()
    this.mesh.material.uniforms.uTime.value = time
  }

  destroy() {
    this.scene.remove(this.mesh)
    this.mesh.geometry.dispose()
    this.mesh.material.dispose()
  }
}
